import React, { useState } from 'react';
import { usePollingApi, fmtKST, fmtPrice } from '../hooks/useApi';
import BotAvatar from './BotAvatar';
import SyncFreshnessBadge from './SyncFreshnessBadge';

const BOT_COLORS = {
  warren_buffett: 'text-blue-400',
  peter_lynch: 'text-green-400',
  ray_dalio: 'text-purple-400',
  cathie_wood: 'text-pink-400',
  jesse_livermore: 'text-yellow-400',
};

const BOT_LABELS = {
  warren_buffett: 'W. Buffett',
  peter_lynch: 'P. Lynch',
  ray_dalio: 'R. Dalio',
  cathie_wood: 'C. Wood',
  jesse_livermore: 'J. Livermore',
};

const CONSENSUS_OUTCOME_LABELS = {
  ok: { label: '정상', color: 'text-green-400' },
  claude_cli_failure: { label: 'claude CLI 실패', color: 'text-red-400' },
  trading_halted: { label: '매매중단 스킵', color: 'text-yellow-400' },
  no_candidates: { label: '후보 없음', color: 'text-gray-500' },
  prefilter_skip: { label: '사전필터 스킵', color: 'text-gray-500' },
  // "관측 없음"(판단 자체가 없었던 정상 상태)과는 달리, 운영 환경에서 observability
  // 소스(daemon.log/스냅샷) 자체가 없거나 끊겨 판단할 수 없는 상태다 (NEX-1295 devpl 반려).
  unknown: { label: '상태 불명', color: 'text-orange-400' },
};

function ConsensusHealthBadge({ health }) {
  if (!health) return null;
  const candidateConditionSkip = health.last_consensus_outcome === 'no_candidates'
    && health.last_consensus_attempt_at
    && health.llm_failure_count_24h === 0
    && /^\[JOINT-BUY\].*BUY.*(?:후보 없음|종목 없음)/.test(health.skip_reason || '');
  const outcome = health.last_consensus_outcome === 'no_candidates' && !health.last_consensus_attempt_at
    ? { label: '관측 없음', color: 'text-gray-500' }
    : candidateConditionSkip ? { label: '후보 없음(합의 조건 미충족)', color: 'text-gray-500' }
    : CONSENSUS_OUTCOME_LABELS[health.last_consensus_outcome] || {
    label: health.last_consensus_outcome || '알수없음',
    color: 'text-gray-400',
  };
  return (
    <span>
      마지막 합의 검토 시각: {health.last_consensus_attempt_at ? fmtKST(health.last_consensus_attempt_at) : '기록 없음'}{' '}
      <span className={`font-medium ${outcome.color}`}>({outcome.label})</span>
      {health.skip_reason && <span> · 사유: {health.skip_reason}</span>}
      {candidateConditionSkip && (
        <span> · 후보 조건 미충족으로 합의를 생략했습니다. 마지막 합의 결과 시각은 유지됩니다.</span>
      )}
      {health.llm_failure_count_24h > 0 && (
        <span className="text-red-400"> · 24h 실패 {health.llm_failure_count_24h}건</span>
      )}
      {health.last_consensus_outcome === 'unknown' && (
        <span className="text-orange-400">
          {' '}· 스냅샷 {health.snapshot_computed_at ? fmtKST(health.snapshot_computed_at) : '없음'}
        </span>
      )}
    </span>
  );
}

function DecisionBadge({ decision, execute }) {
  const colors = {
    BUY: 'bg-green-900/40 text-green-400 border-green-700',
    SELL: 'bg-red-900/40 text-red-400 border-red-700',
    HOLD: 'bg-gray-800 text-gray-400 border-gray-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-bold ${colors[decision] || colors.HOLD}`}>
      {decision} {execute ? '(체결)' : '(보류)'}
    </span>
  );
}

function BotVote({ bot, decision, confidence }) {
  const name = bot?.replace(/_/g, ' ') || bot;
  const label = BOT_LABELS[bot] || name;
  const color = BOT_COLORS[bot] || 'text-gray-400';
  const dcColor = decision === 'BUY' ? 'text-green-400' : decision === 'SELL' ? 'text-red-400' : 'text-gray-500';
  return (
    <div className="flex items-center gap-2 text-xs">
      <BotAvatar botKey={bot} size={20} />
      <span className={`font-medium ${color}`}>{label}</span>
      <span className={dcColor}>{decision}</span>
      <span className="text-gray-500">{confidence?.toFixed(0)}%</span>
    </div>
  );
}

function ConsensusCard({ item, onToggle, isOpen }) {
  const individual = item.individual || [];
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        className="w-full text-left px-4 py-3 hover:bg-gray-750 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-gray-100">
              {item.stock_name || item.ticker}
            </span>
            <span className="text-xs text-gray-500">{item.ticker}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">
              {item.market}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <DecisionBadge decision={item.decision} execute={item.execute} />
            <span className="text-xs text-gray-500">{item.decided_at_unavailable_reason ? '시각 확인 필요' : fmtKST(item.decided_at)}</span>
          </div>
        </div>

        {/* Summary line */}
        <div className="mt-1 flex items-center gap-4 text-xs text-gray-400">
          <span>agree {item.agree_count}/{item.total_count}</span>
          <span>conf {item.confidence?.toFixed(1)}%</span>
          {item.avg_target > 0 && (
            <span>target {fmtPrice(item.avg_target, item.market)}</span>
          )}
          {item.debate_rounds > 0 && (
            <span className="text-yellow-500">debate {item.debate_rounds}round</span>
          )}
          {item.consensus_reached && (
            <span className="text-green-500">합의 도달</span>
          )}
          {item.threshold_variant === 'b' && (
            <span className="text-purple-400 font-medium">B안</span>
          )}
        </div>
      </button>

      {/* Expandable Detail */}
      {isOpen && (
        <div className="border-t border-gray-700 px-4 py-3 space-y-3">
          {/* Bot votes */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-2">봇별 투표</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {individual.map((v, i) => (
                <BotVote key={i} bot={v.bot} decision={v.decision} confidence={v.confidence} />
              ))}
            </div>
          </div>

          {/* Summary text */}
          {item.summary && (
            <div className="text-xs text-gray-400 bg-gray-900/50 rounded px-3 py-2">
              {item.summary}
            </div>
          )}

          {/* Debate history (if available) */}
          {item.debate && item.debate.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">토론 기록</div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {item.debate.map((d, i) => {
                  const dcColor = d.decision === 'BUY' ? 'text-green-400'
                    : d.decision === 'SELL' ? 'text-red-400' : 'text-gray-500';
                  const label = BOT_LABELS[d.bot_name] || d.bot_name;
                  return (
                    <div key={i} className="text-xs flex gap-2 items-start">
                      <span className={`font-medium min-w-[80px] ${BOT_COLORS[d.bot_name] || 'text-gray-400'}`}>
                        {label}
                      </span>
                      <span className={`min-w-[35px] ${dcColor}`}>{d.decision}</span>
                      <span className="text-gray-500 min-w-[30px]">{d.confidence?.toFixed(0)}%</span>
                      <span className="text-gray-600 truncate">{d.comment?.replace(/\[LG.*?\]\s*/, '')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConsensusTab() {
  const [marketFilter, setMarketFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const endpoint = marketFilter
    ? `/api/consensus?market=${marketFilter}&limit=50`
    : '/api/consensus?limit=50';
  const { data: items, loading } = usePollingApi(endpoint, 300000, [marketFilter]);
  const { data: stats } = usePollingApi('/api/consensus/stats', 300000);
  const { data: health } = usePollingApi('/api/consensus/health', 60000);

  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="전체" value={stats?.total || 0} />
        <StatCard label="체결" value={stats?.executed || 0} color="text-green-400" />
        <StatCard label="보류" value={stats?.held || 0} color="text-gray-400" />
        <StatCard
          label="합의율"
          value={`${stats?.consensus_rate || 0}%`}
          sub={`평균 ${stats?.avg_debate_rounds || 0}라운드`}
          color="text-blue-400"
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>마지막 합의 갱신: {stats?.last_updated_at_unavailable_reason ? '원천 시각 오류로 표시 보류' : stats?.last_updated_at ? fmtKST(stats.last_updated_at) : '기록 없음'}</span>
        <span>B안 실험: {stats?.variant_b_count ?? 0}건</span>
        <SyncFreshnessBadge lastSyncedAt={stats?.last_synced_at} />
      </div>
      <div className="text-xs text-gray-500">
        <ConsensusHealthBadge health={health} />
      </div>

      {/* Market filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Market:</span>
        {[null, 'KR', 'US'].map((m) => (
          <button
            key={m || 'ALL'}
            onClick={() => setMarketFilter(m)}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              marketFilter === m
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {m || 'ALL'}
          </button>
        ))}
      </div>

      {/* Consensus List */}
      {loading && !items ? (
        <div className="text-center text-gray-500 py-8">로딩 중...</div>
      ) : !items || items.length === 0 ? (
        <div className="text-center text-gray-500 py-8">합의 데이터 없음</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <ConsensusCard
              key={item.id}
              item={item}
              isOpen={expandedId === item.id}
              onToggle={() => handleToggle(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-gray-100' }) {
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}
